import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadLatestReport,
  loadReportOptions,
  requestReport,
  signedReportDownload,
} from './xomics-webserver-client';
import './webserver-shared.css';

const POLL_MS = 5000;
const OPEN_STATUSES = new Set(['queued', 'processing', 'running', 'pending']);

// Report generation for one finished job of any webserver. The report types
// offered are the ones XOmics binds to the job's pipeline (reportTypes from the
// catalogue, confirmed by /reports/options); nothing here names a report type.
export default function WebserverReportPanel({ serviceCode, token, job, reportTypes = [] }) {
  const [options, setOptions] = useState(null);
  const [reportType, setReportType] = useState('');
  const [language, setLanguage] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const finished = job?.status === 'completed';
  const reference = job?.upload_uuid && job?.job_uuid ? job : null;

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const refreshLatest = useCallback(async () => {
    if (!reference || !token) return null;
    const latest = await loadLatestReport(serviceCode, token, reference);
    setReport(latest);
    if (!latest || !OPEN_STATUSES.has(String(latest.status).toLowerCase())) stopPolling();
    return latest;
  }, [serviceCode, token, reference?.upload_uuid, reference?.job_uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    stopPolling();
    setOptions(null);
    setReport(null);
    setError('');
    if (!finished || !reference || !token || reportTypes.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [choices, latest] = await Promise.all([
          loadReportOptions(serviceCode, token, reference),
          loadLatestReport(serviceCode, token, reference),
        ]);
        if (cancelled) return;
        setOptions(choices);
        setReportType(choices.reportTypes?.[0]?.code || '');
        setLanguage((choices.languages || []).find((item) => item.code === 'en')?.code
          || choices.languages?.[0]?.code || '');
        setReport(latest);
        if (latest && OPEN_STATUSES.has(String(latest.status).toLowerCase())) {
          pollRef.current = setInterval(() => { refreshLatest().catch(() => {}); }, POLL_MS);
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || 'Report options could not be loaded');
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [serviceCode, token, finished, reference?.upload_uuid, reference?.job_uuid, reportTypes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (reportTypes.length === 0) return null;

  if (!finished) {
    return (
      <div className="WSX-panel" data-testid="wsx-report-panel">
        <div className="WSX-panel-title"><i className="fas fa-file-pdf" /> Report</div>
        <p className="WSX-muted">A report can be generated once this analysis has finished.</p>
      </div>
    );
  }

  const generate = async () => {
    setBusy(true);
    setError('');
    try {
      const created = await requestReport(serviceCode, token, reference, {
        content_type_code: reportType,
        language_code: language,
      });
      setReport(created.report || null);
      stopPolling();
      pollRef.current = setInterval(() => { refreshLatest().catch(() => {}); }, POLL_MS);
    } catch (requestError) {
      setError(requestError.message || 'The report could not be requested');
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setError('');
    try {
      const signed = await signedReportDownload(serviceCode, token, reference, report.uuid);
      window.open(signed.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (downloadError) {
      setError(downloadError.message || 'The report could not be downloaded');
    }
  };

  const status = String(report?.status || '').toLowerCase();
  return (
    <div className="WSX-panel" data-testid="wsx-report-panel">
      <div className="WSX-panel-title"><i className="fas fa-file-pdf" /> Report</div>
      {options && (
        <div className="WSX-row">
          {(options.reportTypes || []).length > 1 && (
            <select className="WSX-field-input" value={reportType} onChange={(event) => setReportType(event.target.value)}>
              {options.reportTypes.map((item) => (
                <option key={item.code} value={item.code}>{item.displayName || item.code}</option>
              ))}
            </select>
          )}
          <select className="WSX-field-input" value={language} onChange={(event) => setLanguage(event.target.value)}>
            {(options.languages || []).map((item) => (
              <option key={item.code} value={item.code}>{item.nativeName || item.name || item.code}</option>
            ))}
          </select>
          <button
            type="button"
            className="WSX-button"
            onClick={generate}
            disabled={busy || !reportType || !language || OPEN_STATUSES.has(status)}
          >
            {OPEN_STATUSES.has(status) ? 'Generating...' : 'Generate report'}
          </button>
        </div>
      )}
      {report && (
        <p className="WSX-muted">
          Latest report: {report.reportType?.displayName || report.reportType?.code || 'report'}, {report.status}
          {report.errorMessage ? ` (${report.errorMessage})` : ''}
        </p>
      )}
      {status === 'done' && (
        <button type="button" className="WSX-button" onClick={download}>
          <i className="fas fa-download" /> Download report
        </button>
      )}
      {error && <p className="WSX-error">{error}</p>}
    </div>
  );
}
